import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  useGetAllCourseListMutation,
  useGetAssignmentFoldersMutation,
  useGetAllAssignedViaMutation,
  useGetAllAssignmentStatusMutation
} from '../../features/authentication/authApiSlice';
import PropTypes from 'prop-types';

const DataContext = createContext();

export const DataProvider = ({
  children,
  loadCourses = true,
  loadFolders = true,
  loadAssignedVia = true,
  loadStatus = true
}) => {
  const [courseData, setCourseData] = useState([]);
  const [folderData, setFolderData] = useState([]);
  const [assignedViaData, setAssignedViaData] = useState([]);
  const [assignmentStatusData, setAssignmentStatusData] = useState([]);
  const [reloadFolders, setReloadFolders] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [getAllCourses] = useGetAllCourseListMutation();
  const [getAllFolders] = useGetAssignmentFoldersMutation();
  const [getAllAssignedVia] = useGetAllAssignedViaMutation();
  const [getAllAssignmentStatus] = useGetAllAssignmentStatusMutation();

  const mathSkillCourses = [14, 26, 101];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsDataLoaded(false);

      try {
        const promises = [
          loadCourses ? getAllCourses().unwrap() : Promise.resolve([]),
          loadFolders ? getAllFolders().unwrap() : Promise.resolve([]),
          loadAssignedVia ? getAllAssignedVia().unwrap() : Promise.resolve([]),
          loadStatus ? getAllAssignmentStatus().unwrap() : Promise.resolve([])
        ];

        const [courses, folders, assignedVia, statuses] = await Promise.allSettled(promises);

        setCourseData(courses.status === 'fulfilled' ? courses.value : []);
        setFolderData(folders.status === 'fulfilled' ? folders.value : []);
        setAssignedViaData(assignedVia.status === 'fulfilled' ? assignedVia.value : []);
        setAssignmentStatusData(statuses.status === 'fulfilled' ? statuses.value : []);

        setIsDataLoaded(true);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [loadCourses, loadFolders, loadAssignedVia, loadStatus]);

  useEffect(() => {
    const reloadFoldersAsync = async () => {
      if (!reloadFolders) return;

      try {
        const folders = await getAllFolders().unwrap();
        setFolderData(folders);
      } catch (error) {
        console.log(error);
      } finally {
        setReloadFolders(false);
      }
    };

    reloadFoldersAsync();
  }, [reloadFolders, getAllFolders]);

  return (
    <DataContext.Provider
      value={{
        courseData,
        folderData,
        assignedViaData,
        assignmentStatusData,
        isDataLoaded,
        isLoading,
        setReloadFolders,
        mathSkillCourses
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

DataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  loadCourses: PropTypes.bool,
  loadFolders: PropTypes.bool,
  loadAssignedVia: PropTypes.bool,
  loadStatus: PropTypes.bool
};

export const useData = () => useContext(DataContext);